import { useCallback, useEffect, useState, useRef } from 'react';
import {
  Outlet,
  useParams,
  generatePath,
  useMatch,
  useNavigate,
  NavLink,
  useOutletContext,
  useLocation,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  Tabs,
  IconLoader,
  IconArrowLeft,
  Account,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useGetCustomersInfoDictionariesQuery } from '../../store/dictionaries/services';
import {
  clearGeneralState,
  setBalances,
  setConvertedToIndividual,
} from './store/slices/general-info';
import { clearOverviewState } from './store/slices/overview/overview';

import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../customers/store/slices/statements';
import {
  useLazyGetIndividualUserQuery,
  useLazyGetIndividualAccountBalanceQuery,
  useLazyGetMainChannelQuery,
  useLazyGetCustomerActiveLocksQuery,
  useLazyGetIndividualUserScoreQuery,
} from './store/services/individual';
import { useLazyGetCustomersTopUpQuery } from '../customers/store/services/customers';
import { clearAccountsState } from './store/slices/accounts/accounts';
import { clearStatisticState } from '../customers/store/slices/statistic';
import { clearDocumentsState } from '../customers/store/slices/documents/documents';
import { clearTariffsState } from '../customers/store/slices/tariffs/tariff';
import { clearAmlState } from './store/slices/aml/aml';
import { clearDevicesState } from './store/slices/devices';
import { clearExternalAccounts } from '../customers/store/slices/external-accounts';
import { clearBeneficiariesState } from '../customers/store/slices/beneficiaries/beneficiaries';
import {
  clearLocksState,
  clearLogsState,
  setOrganizationId,
} from './store/slices/security';
import {
  Balance,
  CustomerScore,
  CustomerType,
  OrganizationType,
} from '../customers/types';
import CustomerRightSide from '../../components/partial/info-right-side/info-right-side';
import IndividualCustomerRightSideContent from '../../components/individual/right-side-content';

import ROUTES from '../../routes';

import cn from './information.module.css';

const IndividualCustomerInformation = () => {
  const { customerId } = useParams();
  const {
    general: {
      baseInformation,
      totalBalance,
      topUpOptions,
      convertedToIndividual,
      mainChat,
      activeLock,
    },
    aml: { aml },
  } = useAppSelector((state) => state.individualCustomers.info);
  const { cardsEnabled } = useAppSelector(
    (state) => state.customersDictionaries,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const [query, setQuery] = useState<string | undefined>(undefined);
  const [notFound, setNotFound] = useState(false);
  const [withOverflow, setWithOverflow] = useState(false);
  const [hiddenRoutes, setHiddenRoutes] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([]);
  const [tabs, setTabs] = useState<
    { name: string; path: string; hidden?: boolean }[]
  >([
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Security',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.LOCKS_LIST),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ]);

  const defaultTabs = [
    {
      name: 'Overview',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Accounts',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'External Integration',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
      ),
    },
    {
      name: 'Cards',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_CARDS, {
        customerId: customerId as string,
      }),
      hidden: !cardsEnabled,
    },
    {
      name: 'AML & KYC',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Tariff',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_TARIFFS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Security',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.LOCKS_LIST),
    },
    {
      name: 'Documents',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_DOCUMENTS_LIST,
      ),
    },
    {
      name: 'Statistics',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_STATISTICS, {
        customerId: customerId as string,
      }),
    },
    {
      name: 'Beneficiaries',
      path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES, {
        customerId: customerId as string,
      }),
      hidden: !activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST),
    },
  ];

  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const tabsWrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const [getUser, { isLoading, isFetching }] = useLazyGetIndividualUserQuery();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { search } = useLocation();

  const [getBalance] = useLazyGetIndividualAccountBalanceQuery();
  const [getCustomersTopUp] = useLazyGetCustomersTopUpQuery();
  const [getMainChannel] = useLazyGetMainChannelQuery();
  const [getCustomerActiveLock] = useLazyGetCustomerActiveLocksQuery();
  const [getIndividualScore] = useLazyGetIndividualUserScoreQuery();

  useGetCustomersInfoDictionariesQuery();

  const getBalancesAction = useCallback(async (accounts?: Account[]) => {
    if (accounts && accounts.length) {
      const balancesResponse = await Promise.all(
        accounts.map(async (item) => {
          const response = await getBalance(item.id);

          return response?.data?.data;
        }),
      );

      const formatted: Balance[] = [];

      balancesResponse.forEach((item) => {
        item?.edges.forEach((balance) => {
          formatted.push(balance.node);
        });
      });

      dispatch(
        setBalances({
          balances: formatted,
          totalBCBalances: (accounts
            ? accounts.map((item) => item.totalBalanceBC)
            : [0]) as number[],
        }),
      );
    }
  }, []);

  const getUserAction = useCallback(async () => {
    const { error } = (await getUser(customerId as string)) as {
      data: any;
      error: any;
    };
    const amlUpdateId = localStorage.getItem('amlUpdateId');

    if (error?.data) {
      dispatch(setConvertedToIndividual(false));
      if (error.data.statusCode === 400) {
        setNotFound(true);
      }
    } else if (
      !error &&
      !amlUpdateId &&
      activePermissions?.includes(
        PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
      )
    ) {
      (await getIndividualScore(customerId as string)) as {
        data: { data: CustomerScore };
      };
    }
  }, []);

  useEffect(() => {
    if (query === undefined) {
      setQuery(search);
    }
  }, [query, search]);

  useEffect(() => {
    if (baseInformation) {
      getCustomerActiveLock(baseInformation?.userId as string);
    }
  }, [baseInformation]);

  useEffect(() => {
    if (customerId) {
      getUserAction();
      getMainChannel(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (baseInformation) {
      getBalancesAction(baseInformation?.accounts);

      if (!topUpOptions) {
        getCustomersTopUp({});
      }
    }
  }, [baseInformation]);

  useEffect(() => {
    if (convertedToIndividual) {
      getUserAction();
    }
  }, [convertedToIndividual]);

  const onClear = () => {
    dispatch(clearGeneralState());
    dispatch(clearOverviewState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearAccountsState());
    dispatch(clearStatisticState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearAmlState());
    dispatch(clearExternalAccounts());
    dispatch(clearDevicesState());
    dispatch(clearLocksState());
    dispatch(clearLogsState());
    dispatch(setOrganizationId(''));
    dispatch(clearBeneficiariesState());
  };

  useEffect(() => {
    if (match && customerId) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW, { customerId }),
      );
    }
  }, [match, customerId]);

  useEffect(
    () => () => {
      onClear();
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      tabsWrapperRef &&
      tabsWrapperRef.current &&
      tabsRef &&
      tabsRef.current &&
      baseInformation &&
      baseInformation?.type !== OrganizationType.COMPANY_OFFICIAL &&
      !isLoading
    ) {
      if (
        tabsRef?.current?.scrollWidth > tabsWrapperRef?.current?.offsetWidth
      ) {
        setHiddenRoutes(defaultTabs.slice(-4));
        setTabs(defaultTabs.slice(0, -4));
        setWithOverflow(false);
      } else {
        setHiddenRoutes([]);
        setTabs(defaultTabs);
        setWithOverflow(true);
      }
    }
  }, [baseInformation, isLoading]);

  useEffect(() => {
    if (
      baseInformation &&
      baseInformation.type === OrganizationType.COMPANY_OFFICIAL
    ) {
      setTabs([
        {
          name: 'Overview',
          path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW, {
            customerId: customerId as string,
          }),
        },
        {
          name: 'AML & KYC',
          path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_AML, {
            customerId: customerId as string,
          }),
          hidden: !activePermissions?.includes(
            PermissionEnum.INDIVIDUAL_CUSTOMERS_AML_LIST,
          ),
        },
        {
          name: 'Documents',
          path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
            customerId: customerId as string,
          }),
        },
        {
          name: 'Security',
          path: generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY, {
            customerId: customerId as string,
          }),
          hidden: !activePermissions?.includes(PermissionEnum.LOCKS_LIST),
        },
      ]);
    }
  }, [baseInformation]);

  useEffect(() => {
    let title =
      baseInformation?.firstName || baseInformation?.lastName
        ? `${baseInformation?.firstName} ${baseInformation?.lastName || ''}`
        : 'Individual Customers';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation?.firstName, baseInformation?.lastName]);

  let content;

  if (isLoading || (isFetching && !baseInformation)) {
    content = (
      <div className={cn.individualCustomerLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading && notFound) {
    content = (
      <div className={cn.userNotFound}>
        <div className={cn.userNotFoundTop}>
          <NavLink
            to={`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${query || ''}`}
            className={cn.backLink}
            onClick={() => onClear()}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        <div
          className={cn.userNotFoundContent}
        >{`Client with identifier ${customerId} not found`}</div>
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.INDIVIDUAL_CUSTOMERS_LIST) &&
    !notFound
  ) {
    content = (
      <>
        <div className={cn.individualCustomerLeft}>
          <div className={cn.linkWrapper}>
            <NavLink
              to={`${ROUTES.CUSTOMERS_INDIVIDUAL_LIST}${query || ''}`}
              className={cn.backLink}
              onClick={() => onClear()}
            >
              <IconArrowLeft width={13} height={11} />
              Back
            </NavLink>
          </div>
          <div
            className={`${cn.individualCustomerTabs} ${
              baseInformation?.type === OrganizationType.COMPANY_OFFICIAL
                ? cn.cofTabs
                : ''
            }`}
            ref={tabsWrapperRef}
          >
            <Tabs
              tabs={tabs}
              hiddenRoutes={hiddenRoutes}
              itemClassName={cn.customerTab}
              wrapperClassName={`${cn.customerTabs} ${withOverflow ? cn.tabsWithOverflow : ''}`}
              wrapperRef={tabsRef}
              enableRouting
            />
          </div>

          <Outlet context={{ sendMessage }} />
        </div>
        <div className={cn.individualCustomerRight}>
          <CustomerRightSide
            customerId={customerId as string}
            risk={aml?.totalRiskScore}
            balance={totalBalance?.balance as number}
            tariff={baseInformation?.tariff as string}
            customerType={CustomerType.INDIVIDUAL}
            type={baseInformation?.type}
            name={`${baseInformation?.firstName} ${baseInformation?.lastName}`}
            mainChat={mainChat}
            status={baseInformation?.status}
          >
            <IndividualCustomerRightSideContent
              firstName={baseInformation?.firstName as string}
              lastName={baseInformation?.lastName as string}
              dateOfBirth={baseInformation?.dateOfBirth as string}
              phone={baseInformation?.phone as string}
              email={baseInformation?.email as string}
              type={baseInformation?.type}
              activeLock={activeLock}
              customerId={customerId}
              totpEnabled={baseInformation?.totpEnabled}
            />
          </CustomerRightSide>
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return <div className={cn.individualCustomer}>{content}</div>;
};

export default IndividualCustomerInformation;
